import { defineStore } from 'pinia';
import { User } from '@/api/user.js';
import { Employees } from '@/api/employees.js';
import JwtService from '@/common/jwt.service.js';
import { getTimezone } from '@/utils/helpers/timezone.js';
import { loadHelpSettings } from '@/composables/useHelpSettings.js';
import { buildRoleFilterConditions } from '@/utils/userFilterRoles.js';
import { restrictedRolesAccess } from '@/services/roles/roles.js';

export const userStore = defineStore('storeUser', {
  state: () => ({
    me: {
      id: '',
      name: '',
      lang: {},
      active_company: {},
      active_company_id: '',
      companies: [],
      approved_accounts: {},
      details: '',
      email: '',
      photos: [],
      properties: [],
      role: {},
      forbidden_actions: []
    },
    roles: [],
    languages: [],
    userById: null,
    usersCompany: [],
    usersQuery: [],
    usersQueryMax: 0,
    usersQueryFiltered: null,
    driversQuery: [],
    driversQueryMax: 0,
    driversQueryFiltered: null,
    employeesQuery: [],
    employeesQueryMax: 0,
    employeesQueryFiltered: null,
    controllersQuery: [],
    controllersQueryMax: 0,
    controllersQueryFiltered: null,
    exist: false,
    typeOrganization: [],
    timezones: [],
    payModules: [],
    tariffType: [],
    config: {},
    users: [],
    sendTypes: [],
    filter: {
      integrator: null,
      companies: [],
      roles: [],
    },
    /** Снимок опций ролей на момент «Сохранить» — для role_key / role_id в query. */
    filterRoleOptions: [],
    count: 0,
    sortField: '',
    sortDesc: '',
    smsGatewayType: [],
    timezone_key: (() => {
      const stored = localStorage.getItem('USER_TIME_ZONE');
      if (stored) return stored;
      const detected = getTimezone();
      localStorage.setItem('USER_TIME_ZONE', detected);
      return detected;
    })(),
    operationsType:[],
    maps: {
      web: [],
      ios: [],
      android: [],
    },
    companyBlockTypes: [],
    terminalBlockTypes: [],
    disabledRole: false,
    notRole: false,
  }),

  actions: {
    setExist(data) {
      this.exist = data;
    },
    async login(form) {
      try {
        const response = await User.login(form);
        if (response && response.status === 200) {
          JwtService.setToken('auth', true);
          await loadHelpSettings();
        }
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },
    async getMe() {
      try {
        const response = await User.getMe();
        this.setExist(true);
        this.saveRole(response)
        this.me = response;
        await loadHelpSettings();
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    getUserInfo(id) {
      User.getUserId(id).then((res) => {
        resolve(res);
      });
    },

    async resetPassword(form) {
      try {
        const response = await User.resetPassword(form);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async changeMyPassword(form) {
      try {
        const response = await User.changeMyPassword(form);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async clearUsersFilters() {
      this.filter.status = null;
      this.filter.integrator = null;
      this.filter.companies = [];
      this.filter.roles = [];
      this.filterRoleOptions = [];

      const clearData = {
        companies: [],
        count: 100,
        field: '',
        fields: [],
        from: 0,
        model: 'users',
        value: '',
        conditions: []
      };
      try {
        await this.getUsersQuery(clearData);
      } catch (error) {
        console.error('Error during clearing users:', error);
      }
    },

    async clearEmployeesFilters() {
      this.filter.status = null;
      this.filter.integrator = null;
      this.filter.companies = [];
      this.filter.roles = [];

      const clearData = {
        companies: [],
        count: 100,
        field: '',
        fields: [],
        from: 0,
        model: 'employees',
        value: '',
        conditions: []
      };
      try {
        await this.getUsersQuery(clearData);
      } catch (error) {
        console.error('Error during clearing employees:', error);
      }
    },

    saveRole(data) {
      const isRole = data.role.key

      this.disabledRole =
        isRole !== 'ADMIN' &&
        isRole !== 'SUPERVISOR' &&
        isRole !== 'TARIFF_EDITOR' &&
        isRole !== 'CONTROLLER' &&
        isRole !== 'EDITOR'
      this.notRole =
        isRole === 'ADMIN' ||
        isRole === 'SUPERVISOR' ||
        isRole === 'TARIFF_EDITOR' ||
        isRole === 'CONTROLLER'
    },

    getAllProperties() {
      return User.getAllProperties().then((res) => {
        this.languages = res.filter((el) => el.type === 'languages');
        this.roles = res.filter((el) => el.type === 'roles');
        this.typeOrganization = res.filter((el) => el.type === 'dealer_types');
        this.timezones = res.filter((el) => el.type === 'timezones');
        this.payModules = res.filter((el) => el.type === 'payment_types');
        this.tariffType = res.filter((el) => el.type === 'tariff_type');
        this.sendTypes = res.filter((el) => el.type === 'send_types');
        this.smsGatewayType = res.filter((el) => el.type === 'sms_gateway_type');
        this.operationsType = res.filter((el) => el.type === 'billing_operation_type');
        this.maps.web = res.filter((el) => el.type === 'web_maps');
        this.maps.ios = res.filter((el) => el.type === 'ios_maps');
        this.maps.android = res.filter((el) => el.type === 'android_maps');
        this.companyBlockTypes = res.filter((el) => el.type === 'block_type');
        this.terminalBlockTypes = res.filter((el) => el.type === 'terminal_block_type');
        return res;
      });
    },
    async getCompanyUsers(activeCompanyId) {
      try {
        const response = await User.getCompanyUsers(activeCompanyId);
        this.usersCompany = response.list;
        return response.list;
      } catch (e) {
        console.log(e);
      }
    },
    getAllConfigs() {
      return User.getAllConfigs().then((res) => {
        this.config = res;
      });
    },
    async getFilterQuery(formData) {
      try {
        const response = await User.postFilterQuery(formData);
        this.usersQuery = response.list;
        this.usersQueryFiltered = response.list;
        this.usersQueryMax = response.max;
        return response;
      } catch (e) {
        throw new Error(e);
      }

    },
    async postJsonUsers({
      companies = [],
      value,
      field,
      model = 'users'
    }) {
      try {
        const requestData = {
          from: 0,
          count: 90000,
          value,
          field,
          companies,
          model
        };

        const response = await this.getUsersQuery(requestData);

        return response;
      } catch (e) {
        throw new Error(e);
      }
    },
    async getUsersQuery({
      from = 0,
      count = 100,
      companies = [],
      conditions = [],
      filed,
      value,
      model = 'users'
    }) {
      try {
        const isDriversList = model === 'drivers';
        const apiModel = isDriversList ? 'users' : model;

        const formData = {
          from,
          count,
          filed,
          value,
          model: apiModel
        };

        formData.conditions = [];
        formData.companies = [];

        if (this.sortDesc !== '') {
          formData.sortDesc = this.sortDesc;
          formData.sortField = this.sortField;
        }

        if (this.filter.integrator) {
          formData.dealer = { id: this.filter.integrator };
        }

        if (this.filter.companies.length) {
          formData.companies = this.filter.companies.map((id) => {
            return { id };
          });
        }

        if (isDriversList) {
          formData.conditions.push({
            field: 'is_driver',
            value: 'true'
          });
        } else if (apiModel === 'users') {
          formData.exclude_role_keys = [...restrictedRolesAccess];
          const roleConditions = buildRoleFilterConditions(
            this.filter.roles,
            this.filterRoleOptions
          );
          if (roleConditions.length) {
            formData.conditions.push(...roleConditions);
          }
        }

        formData.conditions = [...formData.conditions, ...conditions];
        formData.companies = [...formData.companies, ...companies];

        const response = await User.postUsersQuery(formData);

        if (count !== 90000) {
          if (model === 'employees') {
            this.employeesQuery = response.list;
            this.employeesQueryFiltered = response.list;
            this.employeesQueryMax = response.max;
          } else if (model === 'controllers') {
            this.controllersQuery = response.list;
            this.controllersQueryFiltered = response.list;
            this.controllersQueryMax = response.max;
          } else if (isDriversList) {
            this.driversQuery = response.list;
            this.driversQueryFiltered = response.list;
            this.driversQueryMax = response.max;
          } else {
            const list = response.list || [];
            this.usersQuery = list;
            this.usersQueryFiltered = list;
            this.usersQueryMax = response.max;
          }
        }

        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async createUser(form) {
      const response = await User.createUser(form);
      return response;
    },

    async importDriversCsv(payload) {
      return User.importDriversCsv(payload);
    },

    async importUsersCsv(payload) {
      return User.importUsersCsv(payload);
    },

    async filterUser(data) {
      try {
        const response = await this.getUsersQuery({
          from: 0,
          count: 100,
          value: data?.value,
          filed: data?.valueFilter?.value || '',
          model: 'users'
        });
        return response;
      } catch (e) {
        throw new Error(e);
      }
      // this.usersQueryFiltered = this.usersQuery.filter(val => {
      //     let filteredItem = val
      //     const type = data.valueFilter?.value || 'name'
      //     const value = data.value || ''
      //     const role = data.filter.value
      //     if (role) {
      //         if (val.companies && val.companies.length) {
      //             if (val.companies[0].role.value !== role) {
      //                 filteredItem = null
      //             }
      //         } else {
      //             filteredItem = null
      //         }
      //
      //     }
      //
      //     if (filteredItem) {
      //         return filteredItem[type] && String(filteredItem[type]).toLowerCase().includes(String(value).toLowerCase())
      //     }
      //
      // })
    },

    async deleteUserCompany({ ids, companies, active_company_id, queryModel = 'users' }) {
      try {
        const response = await User.deleteUserCompany({ ids, companies });
        let companiesArray = [];
        if (active_company_id) {
          companiesArray = [{ id: active_company_id }];
        }

        await this.getUsersQuery({ from: 0, count: 100, companiesArray, model: queryModel });
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async deleteUser({ ids, companies = [], active_company_id, queryModel = 'users' }) {
      try {
        const response = await User.deleteUser({ ids, companies });
        let companiesArray = [];
        if (active_company_id) {
          companiesArray = [{ id: active_company_id }];
        }

        await this.getUsersQuery({ from: 0, count: 100, companies: companiesArray, model: queryModel });
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    /** Массовое удаление: одна заявка DELETE admin/users с телом [{ id }, ...] */
    async deleteUsersBulk(payload, { active_company_id, queryModel = 'users' } = {}) {
      try {
        const response = await User.deleteUsersBulk(payload);
        let companiesArray = [];
        if (active_company_id) {
          companiesArray = [{ id: active_company_id }];
        }

        await this.getUsersQuery({ from: 0, count: 100, companies: companiesArray, model: queryModel });
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    /** Массовое удаление сотрудников интегратора: DELETE admin/employeees */
    async deleteEmployeesBulk(payload, { active_company_id } = {}) {
      try {
        const response = await Employees.deleteEmployeesBulk(payload);
        let companiesArray = [];
        if (active_company_id) {
          companiesArray = [{ id: active_company_id }];
        }

        await this.getUsersQuery({
          from: 0,
          count: 100,
          companies: companiesArray,
          model: 'employees'
        });
        return response;
      } catch (e) {
        throw e;
      }
    },

    async updateUser(form) {
      try {
        const response = await User.updateUser(form);
        if (
          this.usersQueryFiltered === null ||
          !this.usersQueryFiltered.length
        ) {
          this.usersQueryFiltered = JSON.parse(
            JSON.stringify(this.usersQuery)
          );
        }
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async checkCreateUserFreeLogin(payload) {
      try {
        const response = await User.checkCreateUserFreeLogin(payload);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async checkUpdateUserFreeLogin(payload) {
      try {
        const response = await User.checkUpdateUserFreeLogin(payload);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async inviteAllCompany(form) {
      try {
        const response = await User.inviteAll(form);
        return response;
      } catch (e) {
        throw new Error(e);
      }
    },

    async WEBSOCKET_CHANGE({ id, operation }) {
      if (operation === 'DELETE') return this.DELETE(id);

      const data = await User.getUserId(id);
      this.userById = data[0];
      operation === 'POST' ? this.ADD(data[0]) : this.UPDATE(data[0]);
    },
    async getUserByIdHandler({ id, activeCompanyId }) {
      const data = await User.getUserId(id, activeCompanyId);
      this.userById = data[0];
    },
    async setUserByIdHandler(formData, activeCompanyId) {
      const data = await User.setUserId(formData, activeCompanyId);
      this.userById = data[0];
    },
    ADD(user) {
      if (!this.usersQueryFiltered.some((x) => x.id === user.id)) {
        this.usersQuery = this.usersQueryFiltered = [
          ...this.usersQueryFiltered,
          user
        ];
      }
    },
    UPDATE(user) {
      const idx = this.usersQueryFiltered.findIndex(
        (elem) => elem.id === user.id
      );

      if (idx === -1) return;

      this.usersQuery = this.usersQueryFiltered = [
        ...this.usersQueryFiltered.slice(0, idx),
        user,
        ...this.usersQueryFiltered.slice(idx + 1)
      ];
    },
    DELETE(id) {
      this.usersQuery = this.usersQueryFiltered =
        this.usersQueryFiltered.filter((item) => item.id !== id);
    },
    SET_USERS() {
      this.usersQuery = [];
    },
    SET_COMPANY_USERS(final) {
      this.usersCompany = final;
    },
    updateSortedUsers(payload, model = 'users') {
      if (model === 'employees') {
        this.employeesQueryFiltered = payload.list;
      } else if (model === 'controllers') {
        this.controllersQueryFiltered = payload.list;
      } else if (model === 'drivers') {
        this.driversQueryFiltered = payload.list;
      } else {
        this.usersQueryFiltered = payload.list;
      }
    },

    async clearDriversFilters() {
      this.filter.integrator = null;
      this.filter.companies = [];

      const clearData = {
        companies: [],
        count: 100,
        field: '',
        fields: [],
        from: 0,
        model: 'drivers',
        value: '',
        conditions: []
      };
      try {
        await this.getUsersQuery(clearData);
      } catch (error) {
        console.error('Error during clearing drivers:', error);
      }
    },

    async filterDriver(data) {
      try {
        return await this.getUsersQuery({
          from: 0,
          count: 100,
          value: data?.value,
          filed: data?.valueFilter?.value || '',
          model: 'drivers'
        });
      } catch (e) {
        throw new Error(e);
      }
    },

    async clearControllersFilters() {
      this.filter.status = null;
      this.filter.integrator = null;

      const clearData = {
        companies: [],
        count: 100,
        field: '',
        fields: [],
        from: 0,
        model: 'controllers',
        value: '',
        conditions: []
      };
      try {
        await this.getUsersQuery(clearData);
      } catch (error) {
        console.error('Error during clearing controllers:', error);
      }
    },

    async filterController(data) {
      try {
        return await this.getUsersQuery({
          from: 0,
          count: 100,
          value: data?.value,
          filed: data?.valueFilter?.value || '',
          model: 'controllers'
        });
      } catch (e) {
        throw new Error(e);
      }
    },

    async filterEmployee(data) {   
      try {
        return await this.getUsersQuery({
          from: data.from || 0,
          count: data.count || 100,
          value: data?.value,
          filed: data?.valueFilter?.value || '',
          model: 'employees'
        });
      } catch (e) {
        throw new Error(e);
      }
    },
    SET_TIMEZONE_KEY(payload) {
      this.timezone_key = payload;
      localStorage.setItem('USER_TIME_ZONE', payload);
    },
    CLEAR_USER_FILTER() {
      this.filter.integrator = null;
      this.filter.companies = [];
      this.filter.roles = [];
      this.filterRoleOptions = [];
    },

    setUsersRoleFilter(roleIds, roleOptions) {
      this.filter.roles = Array.isArray(roleIds) ? [...roleIds] : [];
      this.filterRoleOptions = Array.isArray(roleOptions)
        ? roleOptions.map(({ id, key, label }) => ({ id, key, label }))
        : [];
    }
  },

  getters: {
    getUsersOfCompany: (state) => state.usersCompany,
    getUsersQueryItems: (state) =>
      state.usersQueryFiltered != null
        ? state.usersQueryFiltered
        : state.usersQuery,
    getDriversQueryItems: (state) =>
      state.driversQueryFiltered != null
        ? state.driversQueryFiltered
        : state.driversQuery,
    getEmployeesQueryItems: (state) =>
      state.employeesQueryFiltered != null
        ? state.employeesQueryFiltered
        : state.employeesQuery,
    getControllersQueryItems: (state) =>
      state.controllersQueryFiltered != null
        ? state.controllersQueryFiltered
        : state.controllersQuery,
    getRoles: (state) => state.roles,
    getExist: (state) => state.exist,
    getTypeOrganization: (state) => state.typeOrganization,
    getTimezones: (state) => state.timezones,
    getPayModules: (state) => state.payModules,
    getTariffType: (state) => state.tariffType,
    getAdminModules: (state) => state.adminModules,
    getCurrentUserRole: (state) => state.me.role,
    getActiveCompanyId: (state) => state.me.active_company_id,
    getSendTypes: (state) => state.sendTypes,
    getUser: (state) => state.userById,
    getSmsGatewayType: (state) => state.smsGatewayType,
    getLang: (state) => state.me.lang.key,
    getTimezoneKey: (state) => state.timezone_key,
    getOperationsType: (state) => state.operationsType,
    getMaps: (state) => state.maps,
    getCompanyBlockTypes: (state) => state.companyBlockTypes,
    getTerminalBlockTypes: (state) => state.terminalBlockTypes,
    getMeActiveCompany: (state) => state.me,
  }
});
